// EDEN · the persistent PHONE + TABLET navigation (a bottom tab bar with icons + a "More" sheet),
// the small-screen counterpart to the desktop sidebar (src/chrome.ts). It lives OUTSIDE #app (its
// own <nav id="eden-tabbar"> sibling in index.html), so page re-renders never touch it. Every tab
// points at an EXISTING route via data-link (routed by the router's global click listener). Shown
// below 1024px when signed in (CSS, gated on body.has-chrome) — the sidebar takes over above.

import { me } from "./state";
import { t } from "./i18n";
import { esc } from "./dom";
import { icon } from "./icons";
import { initials } from "./chrome";
import { sectionsFor, mobileTabs, isActive, type NavLeaf } from "./nav";

function tabbarEl(): HTMLElement | null {
  return document.getElementById("eden-tabbar");
}

function tabHtml(path: string, n: NavLeaf): string {
  const on = isActive(path, n.href);
  return `<a class="tab${on ? " active" : ""}" data-link href="${n.href}"${
    on ? ' aria-current="page"' : ""
  }>${icon(n.icon)}<span class="tab-label">${t(n.tabKey ?? n.key)}</span></a>`;
}

function sheetRow(path: string, n: NavLeaf): string {
  const on = isActive(path, n.href);
  return `<a class="ref-row" data-link href="${n.href}"${on ? ' aria-current="page"' : ""}>
      <span class="ref-ico">${icon(n.icon)}</span>
      <span class="ref-text"><span class="label">${t(n.key)}</span>${
        n.subKey ? `<span class="sub">${t(n.subKey)}</span>` : ""
      }</span>
      <span class="chev" aria-hidden="true">${icon("chevron")}</span>
    </a>`;
}

/** Open/close the "More" sheet by toggling `hidden` on the sheet + backdrop (no re-render needed;
 *  picking an item navigates, which re-renders the bar closed). */
function setSheet(open: boolean): void {
  const bar = tabbarEl();
  if (!bar) return;
  for (const el of bar.querySelectorAll<HTMLElement>(".nav-sheet, .nav-sheet-backdrop")) {
    el.hidden = !open;
  }
  bar.classList.toggle("sheet-open", open);
}

/** Wire the once-only listener. Call from boot() before the router starts (mirrors initChrome). */
export function initMobileNav(): void {
  document.addEventListener("click", (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLElement>("[data-mnav]");
    if (!btn) return;
    e.preventDefault();
    setSheet(btn.dataset.mnav === "more");
  });
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") setSheet(false);
  });
}

/** Paint the tab bar for the current user + route. Called from the router on every navigation. */
export function renderMobileNav(path: string): void {
  const bar = tabbarEl();
  if (!bar) return;
  const user = me();
  const show = !!user && path !== "/signin";
  if (!show || !user) {
    bar.innerHTML = "";
    return;
  }

  const tabsList = mobileTabs(user.auth_role);
  const tabs = tabsList.map((n) => tabHtml(path, n)).join("");

  // The More sheet lists every section/item NOT already a tab (and never the primary CTA, which
  // gets its own full-width button at the top of the sheet).
  const tabHrefs = new Set(tabsList.map((n) => n.href));
  const groups = sectionsFor(user.auth_role)
    .map((s) => {
      const rows = s.items
        .filter((i) => !i.primary && !tabHrefs.has(i.href))
        .map((i) => sheetRow(path, i))
        .join("");
      const head = s.role === "lead" ? t("nav.lead_group") : t(s.key);
      return rows ? `<div class="eden-nav-group">${head}</div><div class="ref-list">${rows}</div>` : "";
    })
    .join("");

  // "More" is active when the open page lives behind it (any non-tab item).
  const moreOn = sectionsFor(user.auth_role)
    .flatMap((s) => s.items)
    .some((i) => !i.primary && !tabHrefs.has(i.href) && isActive(path, i.href));

  const roleLabel = t(user.auth_role === "lead" ? "nav.role_lead" : "nav.role_teacher");
  bar.innerHTML = `
    <div class="nav-sheet-backdrop" data-mnav="close" hidden></div>
    <div class="nav-sheet" role="dialog" aria-label="${esc(t("nav.more"))}" hidden>
      <div class="nav-sheet-head">
        <span class="eden-user"><span class="avatar" aria-hidden="true">${esc(initials(user.name))}</span>
          <span class="who"><span class="name">${esc(user.name)}</span><span class="role">${roleLabel}</span></span></span>
        <button type="button" class="linkbtn" data-mnav="close">${t("nav.close")}</button>
      </div>
      <a class="btn sheet-cta" data-link href="/plan/start">${icon("plus")}<span>${t("week.plan_button")}</span></a>
      ${groups}
      <button type="button" class="btn secondary sheet-signout" data-action="signout">${icon("logout")}<span>${t("common.sign_out")}</span></button>
    </div>
    <div class="tabrow" role="navigation" aria-label="Primary">
      ${tabs}
      <button type="button" class="tab${moreOn ? " active" : ""}" data-mnav="more" aria-haspopup="dialog">
        ${icon("more")}<span class="tab-label">${t("nav.more")}</span>
      </button>
    </div>`;
}
